import { cn } from "@/lib/utils";
import type { Icon } from "@phosphor-icons/react";

/**
 * What a page or a card shows when it has nothing to show — and, through
 * `ErrorState`, when it could not find out.
 *
 * The page layer drew this six different ways (a dashed frame, a padded Card
 * with a CTA, a grey `<p>`…), so the same absence read differently from one
 * screen to the next. One shape now: a duotone icon, a title saying what is
 * missing, a line saying what to do about it, and at most one action.
 *
 * `compact` is for the inside of a card, which already has a frame: no border,
 * less air, a smaller icon. The default is for a page body, where the dashed
 * frame marks the spot the content will take.
 */
export function EmptyState({
  icon: IconComponent,
  title,
  description,
  action,
  compact = false,
  tone = "neutral",
  className,
  ...props
}: {
  icon?: Icon;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  compact?: boolean;
  /** `danger` is `ErrorState`'s — an empty list is not an incident. */
  tone?: "neutral" | "danger";
  className?: string;
} & Omit<React.ComponentProps<"div">, "title">) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        "flex flex-col items-center justify-center text-center",
        compact
          ? "gap-2 px-4 py-8"
          : "gap-3 rounded-lg border border-dashed px-6 py-14",
        className,
      )}
      {...props}
    >
      {IconComponent && (
        <div
          className={cn(
            "flex items-center justify-center rounded-full",
            compact ? "mb-1 size-10" : "mb-1 size-14",
            tone === "danger"
              ? "bg-danger-soft text-danger"
              : "bg-muted text-muted-foreground",
          )}
        >
          <IconComponent
            weight="duotone"
            className={compact ? "size-5" : "size-7"}
            aria-hidden
          />
        </div>
      )}
      <p className={cn("font-semibold", compact ? "text-sm" : "text-base")}>
        {title}
      </p>
      {description && (
        <p
          className={cn(
            "max-w-md text-muted-foreground",
            compact ? "text-xs" : "text-sm",
          )}
        >
          {description}
        </p>
      )}
      {action && (
        <div className="mt-2 flex flex-wrap justify-center gap-2">{action}</div>
      )}
    </div>
  );
}
